/**
 * Slurs on the notehead side, as engravers place them. MuseScore exports slurs without a side
 * (`placement`), and OpenSheetMusicDisplay then draws a slur above whenever a note under it has
 * lyrics: in a choir score, every slur. It follows a side the file gives, so this adds one to
 * each slur that has none, before drawing: stems up at both ends, below; down at both ends,
 * above; mixed, above. A note without a stem (a whole note) takes the other end's stem; with no
 * stem at either end, the slur is left to the drawing.
 */
export function placeSlursOnNoteheadSide(xml: string): string {
  const inserts: { at: number; text: string }[] = [];

  for (const part of xml.matchAll(/<part\b[^>]*>[\s\S]*?<\/part>/g)) {
    const partStart = part.index ?? 0;
    // Open slurs of this part by number: where to add the side, and the starting note's stem.
    const open = new Map<string, { tagEnds: number[]; stem: Stem }>();
    for (const note of part[0].matchAll(/<note\b[\s\S]*?<\/note>/g)) {
      const noteStart = partStart + (note.index ?? 0);
      const stem = stemOf(note[0]);
      for (const slur of note[0].matchAll(/<slur\b[^>]*?(\/?)>/g)) {
        const tag = slur[0];
        const number = /\bnumber="([^"]*)"/.exec(tag)?.[1] ?? '1';
        const type = /\btype="([^"]*)"/.exec(tag)?.[1];
        // Where to insert: just before the tag's closing "/>" or ">".
        const tagEnd = noteStart + (slur.index ?? 0) + tag.length - (slur[1] ? 2 : 1);
        if (type === 'start') {
          if (/\bplacement="/.test(tag)) open.delete(number);
          else open.set(number, { tagEnds: [tagEnd], stem });
        } else if (type === 'stop') {
          const started = open.get(number);
          open.delete(number);
          if (!started || /\bplacement="/.test(tag)) continue;
          const side = placementForStems(started.stem, stem);
          if (!side) continue;
          for (const at of [...started.tagEnds, tagEnd]) inserts.push({ at, text: ` placement="${side}"` });
        }
      }
    }
  }

  if (inserts.length === 0) return xml;
  // From the end, so earlier positions stay valid.
  inserts.sort((a, b) => b.at - a.at);
  let out = xml;
  for (const { at, text } of inserts) out = out.slice(0, at) + text + out.slice(at);
  return out;
}

type Stem = 'up' | 'down' | null;

function stemOf(note: string): Stem {
  const stem = /<stem\b[^>]*>\s*(up|down)\s*<\/stem>/.exec(note)?.[1];
  return stem === 'up' || stem === 'down' ? stem : null;
}

function placementForStems(start: Stem, end: Stem): 'above' | 'below' | null {
  const a = start ?? end;
  const b = end ?? start;
  if (!a || !b) return null;
  return a === 'up' && b === 'up' ? 'below' : 'above';
}
